/**
 * On-device timing probes for the build-11 measurement session.
 *
 * `docs/message-outbox-plan.md` §9 step 1 and
 * `docs/group-create-latency-plan.md` §5 both begin with the same demand:
 * decompose the filmed delays on a real device before any fix is written.
 * The screen-capture overlay (`screenrecord --bugreport`) supplies paint
 * truth; these probes supply the JS-side timeline to correlate against it,
 * via `adb logcat` (`ReactNativeJS`) on device and the console on web.
 *
 * Three properties, in order of importance:
 *
 *   1. **No content can pass through.** The signature accepts a probe name
 *      and numbers — nothing else — and the runtime drops any value that is
 *      not a finite number, so a message body cannot reach a log even by
 *      mistake. This is what lets the probes ship in a build rather than
 *      living on a branch: they cannot say anything the Sentry data policy
 *      would mind, and they never leave the device at all (Sentry Logs are
 *      off; nothing forwards the console).
 *   2. **A probe can never be the failure.** Everything is wrapped; a
 *      throwing console is swallowed. Measurement must not alter or break
 *      the thing being measured.
 *   3. **Import-free**, so the root `node --test` suite can load it — the
 *      same constraint (and reason) as `fix-age.ts`.
 *
 * Deliberately always-on rather than flag-gated: each probe fires on a
 * user-initiated action (a send, a create), so the volume is bounded by taps,
 * and a switch nobody flips is how instrumentation rots. Remove call sites
 * when their question is answered instead.
 */

/** The greppable tag: `adb logcat | grep opncue-probe`. */
const TAG = "[opncue-probe]";

/** Numbers only — the type-level half of property 1. */
export type ProbeExtras = Record<string, number>;

function sanitize(extras: ProbeExtras | undefined): Record<string, number> {
  const clean: Record<string, number> = {};
  if (!extras) return clean;
  for (const key of Object.keys(extras)) {
    const value = extras[key];
    // The runtime half of property 1: anything that is not a finite number
    // is dropped, so a string smuggled past the types never reaches a log.
    if (typeof value === "number" && Number.isFinite(value)) {
      clean[key] = Math.round(value * 100) / 100;
    }
  }
  return clean;
}

/**
 * Record that a named moment happened, with an epoch-ms timestamp so probes
 * from different modules land on one comparable timeline.
 */
export function probe(name: string, extras?: ProbeExtras): void {
  try {
    console.log(TAG, name, JSON.stringify({ at: Date.now(), ...sanitize(extras) }));
  } catch {
    // Property 2: a broken console must not become an app failure.
  }
}

/**
 * Time a span: `const end = probeSpan("create.coords"); ...; end();`
 *
 * Logs once, at the end, with the duration — a span that logs at both ends
 * doubles the noise without adding information, since `at - ms` recovers the
 * start. `end` is idempotent: calling it twice logs once, so a span closed in
 * both a success and a `finally` path cannot double-report.
 */
export function probeSpan(name: string, extras?: ProbeExtras): () => void {
  const startedAt = Date.now();
  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    probe(name, { ...extras, ms: Date.now() - startedAt });
  };
}

/**
 * A safe suffix for a probe NAME, derived from a thrown value.
 *
 * Probes carry only numbers, so anything categorical has to ride in the name —
 * `keyboard.resume.chat`, `chat.photo.fail.TypeError`. The class of a failure is
 * exactly that kind of fact, and it is the one that separates causes needing
 * completely different fixes: `TypeError` is `fetch` failing before a round
 * trip, `ApiError` means the server answered, `SessionExpiredError` means the
 * refresh path.
 *
 * Read from `.name`, never `constructor.name`: a release bundle is minified and
 * a mangled class name would report as noise, while `ApiError` and
 * `SessionExpiredError` both assign `this.name` in their constructors and the
 * built-ins carry theirs on the prototype.
 *
 * Clamped to letters and a fixed length even so. A thrown object can carry any
 * `name` at all, including one built from a server string, and property 1 of
 * this module — no content reaches a log — has to hold for names as well as for
 * extras. Anything else becomes `Other`.
 */
export function probeErrorName(value: unknown): string {
  const name =
    value !== null && typeof value === "object" && "name" in value
      ? String((value as { name: unknown }).name)
      : typeof value;
  return /^[A-Za-z]{1,32}$/.test(name) ? name : "Other";
}
